import { discountFraction } from "@/lib/discount";

import type { ApiModel, ApiModelProviderMapping } from "./api-types";

export type ModelCategoryFilter =
	| "text"
	| "text-to-image"
	| "image-to-image"
	| "video"
	| "embedding"
	| "decisions"
	| "web-search"
	| "vision"
	| "reasoning"
	| "tools"
	| "discounted"
	| "roleplay"
	| "coding"
	| "creative-writing"
	| "translation"
	| "math"
	| "cyber-security"
	| "reverse-engineering"
	| "long-context"
	| "cheapest"
	| "open-source"
	| "premium";

export const LONG_CONTEXT_MIN_TOKENS = 200_000;
export const CHEAPEST_MAX_INPUT_PRICE_PER_M = 0.2;
export const CHEAPEST_MAX_OUTPUT_PRICE_PER_M = 1.5;

export const curatedCategoryModelIds: Record<
	| "roleplay"
	| "coding"
	| "creative-writing"
	| "translation"
	| "math"
	| "cyber-security"
	| "reverse-engineering",
	ReadonlySet<string>
> = {
	roleplay: new Set([
		"deepseek-v3.2",
		"deepseek-v4-pro",
		"deepseek-v4-flash",
		"deepseek-v4.1-flash",
		"deepseek-v4.1-flash-1017",
		"grok-4-3",
		"kimi-k2",
		"kimi-k2.5",
		"kimi-k2.6",
		"glm-4.7",
		"glm-5",
		"glm-5.2",
		"minimax-m3",
		"minimax-text-01",
		"mistral-large-2512",
		"mistral-small-2506",
		"llama-3.3-70b-instruct",
		"llama-4-maverick-17b-instruct",
		"claude-sonnet-5-5",
		"claude-sonnet-5",
		"claude-opus-4-8",
	]),
	coding: new Set([
		"claude-fable-5-1",
		"claude-opus-5-5",
		"claude-fable-5",
		"claude-opus-4-8",
		"claude-sonnet-5-5",
		"claude-sonnet-5",
		"claude-sonnet-4-6",
		"claude-haiku-5-5",
		"claude-haiku-4-5",
		"gpt-5.3-codex",
		"gpt-5.2-codex",
		"gpt-5.1-codex",
		"gpt-5.1-codex-mini",
		"gpt-6-sol",
		"gpt-5.6-sol",
		"gpt-5.6-terra",
		"gpt-5.5",
		"gpt-5.4",
		"gemini-pro-latest",
		"gemini-3.1-pro-preview",
		"gemini-3.5-flash",
		"grok-build-0-1",
		"grok-4-3",
		"kimi-k3.1-pro",
		"kimi-k3.1-pro-highspeed",
		"kimi-k3",
		"kimi-k2.7-code",
		"kimi-k2.7-code-highspeed",
		"kimi-k2.6",
		"qwen3-coder-flash",
		"codestral-2508",
		"devstral-2512",
		"deepseek-v4-pro",
		"deepseek-v4.1-flash",
		"deepseek-v4.1-flash-1017",
		"deepseek-v3.2",
		"glm-5.2",
		"glm-5.1",
		"glm-4.7",
		"minimax-m3",
		"minimax-m2.7",
	]),
	"creative-writing": new Set([
		"claude-opus-5-5",
		"claude-opus-4-8",
		"claude-sonnet-5-5",
		"claude-sonnet-5",
		"claude-fable-5-1",
		"claude-fable-5",
		"kimi-k2.5",
		"kimi-k2.6",
		"kimi-k2",
		"gpt-5.6-sol",
		"gpt-5.5",
		"gemini-pro-latest",
		"gemini-3.1-pro-preview",
		"deepseek-v4-pro",
		"minimax-m3",
		"glm-5.2",
		"mistral-large-2512",
		"grok-4-3",
		"llama-4-maverick-17b-instruct",
		"qwen3.7-max",
	]),
	translation: new Set([
		"gemini-3.1-pro-preview",
		"gemini-3.6-flash",
		"gemini-3.1-flash-lite",
		"gpt-6-luna",
		"gpt-5.6-terra",
		"gpt-5.6-luna",
		"gpt-5.4",
		"gpt-5.4-mini",
		"claude-sonnet-5-5",
		"claude-sonnet-5",
		"claude-haiku-5-5",
		"claude-haiku-4-5",
		"qwen3.7-max",
		"qwen3.7-plus",
		"deepseek-v4-flash",
		"deepseek-v4.1-flash",
		"deepseek-v4.1-flash-1017",
		"mistral-large-2512",
		"seed-1-8-251228",
		"gemma-4-31b-it",
		"kimi-k2.6",
	]),
	math: new Set([
		"gpt-5.6-sol",
		"gpt-5.5-pro",
		"gpt-5.5",
		"gpt-5.4-pro",
		"gpt-5.2-pro",
		"claude-opus-4-8",
		"claude-opus-5-5",
		"claude-fable-5-1",
		"claude-fable-5",
		"gemini-3.1-pro-preview",
		"gemini-pro-latest",
		"deepseek-v4-pro",
		"glm-5.2",
		"minimax-m3",
		"grok-4-20-reasoning",
		"grok-4-3",
		"mimo-v2.6-pro",
	]),
	// Every entry supports tool calling (security agents drive scanners,
	// sandboxes and REA through MCP). Security work splits into two jobs: defensive review (code audit, log
	// triage, threat modelling) where every frontier model performs, and
	// offensive-adjacent analysis (malware in a sandbox, CTFs, exploit
	// triage) where open-weight models are the ones that reliably finish.
	"cyber-security": new Set([
		"claude-opus-4-8",
		"claude-sonnet-5-5",
		"claude-haiku-5-5",
		"gpt-5.5",
		"gpt-6-sol",
		"gpt-5.3-codex",
		"gemini-3.1-pro-preview",
		"gemini-3.5-flash",
		"grok-4-3",
		"grok-build-0-1",
		"deepseek-v4-pro",
		"deepseek-v4.1-flash",
		"deepseek-v4.1-flash-1017",
		"kimi-k3.1-pro",
		"kimi-k3",
		"kimi-k2.7-code",
		"glm-5.2",
		"glm-5.1",
		"qwen3.7-max",
		"qwen3.7-plus",
		"minimax-m3",
		"mimo-v2.6-pro",
	]),
	// Every entry supports tool calling: reverse-engineering agents drive
	// Ghidra, Hopper and IDA through MCP (REA), so a model without tools is
	// useless here regardless of how well it reads assembly.
	"reverse-engineering": new Set([
		"deepseek-v4-pro",
		"deepseek-v4.1-flash",
		"deepseek-v4.1-flash-1017",
		"deepseek-v3.2",
		"kimi-k3.1-pro",
		"kimi-k3",
		"kimi-k2.7-code",
		"kimi-k2.6",
		"glm-5.2",
		"glm-5.1",
		"qwen3.7-max",
		"qwen3.7-plus",
		"minimax-m3",
		"minimax-m2.7",
		"mimo-v2.6-pro",
		"seed-1-8-251228",
		"grok-4-3",
		"grok-build-0-1",
		"gemini-3.1-pro-preview",
		"gpt-5.3-codex",
		"claude-opus-4-8",
	]),
};

export const OPEN_SOURCE_FAMILIES: ReadonlySet<string> = new Set([
	"baidu",
	"meta",
	"deepseek",
	"moonshot",
	"minimax",
	"nousresearch",
	"nvidia",
	"openbmb",
	"xiaomi",
	"zai",
]);

export const OPEN_SOURCE_MODEL_IDS: ReadonlySet<string> = new Set([
	"gpt-oss-120b",
	"gpt-oss-20b",
	"gemma-4-31b-it",
	"gemma-4-26b-a4b-it",
	"mistral-small-2506",
	"devstral-2512",
	"devstral-small-2507",
	"qwen3-235b-a22b-instruct-2507",
	"qwen3-235b-a22b-thinking-2507",
	"qwen3-235b-a22b-fp8",
	"qwen3-32b",
	"qwen3-30b-a3b-instruct-2507",
	"qwen3-4b-fp8",
	"qwen3-next-80b-a3b-thinking",
	"qwen3-next-80b-a3b-instruct",
	"qwen3-coder-480b-a35b-instruct",
	"qwen3-coder-30b-a3b-instruct",
	"qwen3-vl-8b-instruct",
	"qwen3-vl-30b-a3b-instruct",
	"qwen3-vl-30b-a3b-thinking",
	"qwen3-vl-235b-a22b-instruct",
	"qwen3-vl-235b-a22b-thinking",
	"qwen2-5-vl-72b-instruct",
	"qwen2-5-vl-32b-instruct",
	"qwen3.5-9b",
	"qwen3.6-35b-a3b",
]);

// Proprietary models whose family is otherwise open-source (e.g. Meta's
// API-only Muse Spark models alongside the open Llama family)
export const CLOSED_SOURCE_MODEL_IDS: ReadonlySet<string> = new Set([
	"muse-wonder-1.0",
	"muse-spark-1.1",
	"muse-spark-1.2",
	"muse-spark-1.2-contributor",
	"muse-spark-1.3",
	"muse-spark-1.3-contributor",
	// API-only at launch; no open weights published (unlike the K2 family)
	"kimi-k3",
	"kimi-k3.1-pro",
	"kimi-k3.1-pro-highspeed",
]);

// Labs whose model lines are open-weight-first. Broader than the strict
// open-source filter above: it includes families like Alibaba (Qwen) whose
// catalogue mixes open and API-only releases, and keeps API-only launches from
// these labs (e.g. kimi-k3, qwen3.7-max) since they compete on open-lab
// pricing. Used to derive "recommended" model surfaces from the catalogue so
// new releases show up without curation.
export const OPEN_WEIGHT_LAB_FAMILIES: ReadonlySet<string> = new Set(
	Array.from(OPEN_SOURCE_FAMILIES).concat(["alibaba", "mistral"]),
);

export function isTextOutput(output: string[] | null | undefined): boolean {
	return (
		!output?.includes("image") &&
		!output?.includes("video") &&
		!output?.includes("embedding") &&
		!output?.includes("rerank") &&
		!output?.includes("decision") &&
		!output?.includes("search")
	);
}

function isCheapMapping(mapping: ApiModelProviderMapping): boolean {
	if (mapping.inputPrice === null || mapping.outputPrice === null) {
		return false;
	}
	return (
		parseFloat(mapping.inputPrice) * 1e6 <= CHEAPEST_MAX_INPUT_PRICE_PER_M &&
		parseFloat(mapping.outputPrice) * 1e6 <= CHEAPEST_MAX_OUTPUT_PRICE_PER_M
	);
}

export function applyCategoryFilter(
	categoryFilter: ModelCategoryFilter | undefined,
	model: ApiModel,
	mappings: ApiModelProviderMapping[],
): boolean {
	switch (categoryFilter) {
		case "text":
			return isTextOutput(model.output);
		case "text-to-image":
			return model.output?.includes("image") === true;
		case "image-to-image":
			return (
				model.output?.includes("image") === true &&
				mappings.some((m) => m.vision)
			);
		case "video":
			return model.output?.includes("video") === true;
		case "embedding":
			return model.output?.includes("embedding") === true;
		case "decisions":
			return model.output?.includes("decision") === true;
		case "web-search":
			return mappings.some((m) => m.webSearch);
		case "vision":
			return mappings.some((m) => m.vision);
		case "reasoning":
			return mappings.some((m) => m.reasoning);
		case "tools":
			return mappings.some((m) => m.tools);
		case "discounted":
			return mappings.some((m) => discountFraction(m.discount) > 0);
		case "roleplay":
		case "coding":
		case "creative-writing":
		case "translation":
		case "math":
		case "cyber-security":
		case "reverse-engineering":
			return curatedCategoryModelIds[categoryFilter].has(model.id);
		case "long-context":
			return (
				isTextOutput(model.output) &&
				mappings.some((m) => (m.contextSize ?? 0) >= LONG_CONTEXT_MIN_TOKENS)
			);
		case "cheapest":
			return (
				isTextOutput(model.output) &&
				(model.free === true || mappings.some(isCheapMapping))
			);
		case "open-source":
			return (
				isTextOutput(model.output) &&
				!CLOSED_SOURCE_MODEL_IDS.has(model.id) &&
				(OPEN_SOURCE_FAMILIES.has(model.family) ||
					OPEN_SOURCE_MODEL_IDS.has(model.id))
			);
		case "premium":
			return model.premium;
		default:
			return true;
	}
}
